import React, { useState, useRef, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Box from '@mui/material/Box'

import {
  selectPage,
  selectPageIsMine,
  addPositionToPage,
  updatePageAsync,
  zoomByPageAsync,
} from '../pages/pagesSlice'
import {
  addPositionToStikis,
  alignStikisTo,
  updateDirtyStikisAsync,
  createStikiAsync,
  selectAllUnselectedStikisOnPage,
  setAllStikisOnPageUnselectedAsync,
  selectAllSelectedStikisOnPage,
} from './stikisSlice'
import {
  selectUIColorCurrent,
  selectMarqueeIsSelecting,
} from '../ui/uiSlice'

import { pxToEm, clamp } from '../../helpers'
import {
  PAGE_BASE_FONT_SIZE,
  PAGE_ZOOM_PRESETS,
  SELECTION_NUDGE_DIST,
  PAGE_NUDGE_DIST,
  PAGE_SHIFT_NUDGE_DIST,
  STIKI_MIN_LEFT_EDGE,
  STIKI_MAX_LEFT_EDGE,
  STIKI_MIN_TOP_EDGE,
  STIKI_MAX_TOP_EDGE,
} from '../../constants'

import Stiki from './Stiki'
import StikisMover from './StikisMover'
import SelectionBox from './SelectionBox'
import MarqueeEventWrapper from './MarqueeEventWrapper'
import ResizeEventWrapper from './ResizeEventWrapper'
import StikisMoveEventWrapper from './StikisMoveEventWrapper'

const createClasses = (props) => {return {
  stikisField: {
    // id='stikisField'
    position: props.isStatic ? 'relative' : 'fixed',
    inset: 0,
    margin: 0,
    padding: 0,
    overflow: props.isStatic ? 'visible' : 'hidden',
    background: props.isStatic ? 'red' : (theme) => theme.palette.grey[800],
    // TODO - to take account of top menu, set top: 74px,
    '&:focus': {
      outline: 'none',
    },
    '&.relative': {
      background: 'transparent',
      // zIndex: 1,
      position: 'absolute',
      // position: 'relative',
      // width: props.width,
      // height: props.height,
      // border: '1px solid silver',
    }
    // #stikisField.selectable .page {
    //   cursor: crosshair;
    // }

    // #stikisField.selecting .page {
    //   cursor: crosshair;
    // }
  },
  stikisFieldCenter: {
    // id='stikisFieldCenter'
    position: 'absolute',
    margin: 0,
    padding: 0,
    left: '50%',
    top: '50%',
    fontSize: '70%',
  },
  zoomOrigin: {
    // id='zoomOrigin'
    position: 'absolute',
    margin: 0,
    padding: 0,
    left: 0,
    top: 0,
  },
  zoomInMarker: {
    // id='zoomInMarker'
    // className='zoomMarker'
    position: 'absolute',
    zIndex: 2,
    display: 'none',
    opacity: '0.8',
    left: '-5em',
    top: '-5em',
    width: 1,
    height: 1,
    cursor: 'move',

    '& div': {
      // .zoomMarker .t,
      // .zoomMarker .l,
      // .zoomMarker .r,
      // .zoomMarker .b
      width: '0.5em',
      height: '0.5em',
      position: 'absolute',
      opacity: 'inherit',
      backgroundColor: '#e05e74',
    },
    '& .t, & .b': {
      // .zoomMarker .t,
      // .zoomMarker .b {
      left: '3em',
      width: '4em',
    },
    '& .l, & .r': {
      // .zoomMarker .l,
      // .zoomMarker .r {
      top: '3em',
      height: '4em',
    },
    '& .r': {
      // .zoomMarker .r {
      left: '9.5em',
    },
    '& .b': {
      // .zoomMarker .b {
      top: '9.5em',
    },
  },
  pageMover: {
    // id='pageMover'
    // className='cursorWrap'
    position: 'absolute',
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    border: '0 solid black',
    backgroundColor: 'transparent',
    visibility: 'visible',
  },
  page: (leftEdge, topEdge) => ({
    // className='page'
    left: `${leftEdge}em`,
    top: `${topEdge}em`,
    position: 'absolute',
    margin: 0,
    padding: 0,
    // The standard grab/grabbing cursors. This used to say
    // `image-url("grab.cur"), -moz-grab`, which is a Sass asset helper rather
    // than CSS and a prefix dropped years ago, so no cursor ever showed.
    cursor: 'grab',
    '&.moving': {
      // .page.moving {
      cursor: 'grabbing',
    },
  }),
  // While the wheel is turning. It covers everything inside the field, and has to
  // win over the cursors the stikis and their text set for themselves, or zooming
  // with the pointer over a stiki — the usual way to do it — would show nothing.
  zooming: (cursor) => ({
    cursor: cursor,
    '& *': {
      cursor: `${cursor} !important`,
    },
  }),
  panBackground: {
    // id='panBackground'
    position: 'absolute',
    listStyle: 'none',
    left: '-210em',
    top: '-148.5em',
    width: '420em',
    height: '297em',
    overflow: 'hidden',
    background: 'white',
    border: '0 solid #bbc',
    '&.relative': {
      background: 'transparent',
      visibility: 'hidden',
    }
  },
}}

export default function StikisField({ pageId, relativeLayout, width, height, disableZoom, disableMove, isStatic }) {
  const dispatch = useDispatch()
  const domRef = useRef(null)
  const classes = createClasses({relativeLayout, width, height, isStatic})

  useEffect(() => {
    domRef?.current?.focus()
  }, [pageId])

  ///////////////////////////////////////////////
  //
  // Selectors
  //

  const page = useSelector(selectPage(pageId))
  // Panning and zooming stay on for a page someone shared with me: they are how
  // you read it, and `updatePageAsync` drops the save, so the view moves without
  // anything being sent. What does not stay on is the double click, which creates
  // a stiki. The arrow keys are safe either way — their other branches act on
  // selected stikis, and a guest can't select one.
  const isMine = useSelector(selectPageIsMine(pageId))
  const noMove = Boolean(disableMove)
  const noZoom = Boolean(disableZoom)
  const zoom = page.zoom
  const selectedStikisSelector = useMemo(
    () => selectAllSelectedStikisOnPage(pageId),
    [pageId]
  )
  const unselectedStikisSelector = useMemo(
    () => selectAllUnselectedStikisOnPage(pageId),
    [pageId]
  )
  const selectedStikis = useSelector(selectedStikisSelector)
  const unselectedStikis = useSelector(unselectedStikisSelector)
  const currentColor = useSelector(selectUIColorCurrent)
  const isSelecting = useSelector(selectMarqueeIsSelecting)

  ///////////////////////////////////////////////
  //
  // Local state
  //

  const [isMoving, setIsMoving] = useState(false)
  const [hasMoved, setHasMoved] = useState(false)
  // 'zoom-in' or 'zoom-out' while the wheel is turning, otherwise null.
  const [zoomCursor, setZoomCursor] = useState(null)
  const zoomCursorTimer = useRef(null)
  const [moveStart, setMoveStart] = useState({ x: 0, y: 0 })
  const [moveOffset, setMoveOffset] = useState({ x: 0, y: 0 })

  const [selectionPosition, setSelectionPosition] = useState({ x: 0, y: 0 })

  ///////////////////////////////////////////////
  //
  // Helpers
  //

  const focusDomRef = () => {
    domRef.current.focus()
  }

  // Both of these move stikis, so neither runs on a page shared with me. A guest
  // has no way to select a stiki in the first place, but that is a fact about
  // other components; this doesn't depend on it staying true.
  const chuckSelection = (direction) => {
    if (!isMine) {
      return
    }

    const [side, position] = {
      ArrowLeft: ['RIGHT', STIKI_MIN_LEFT_EDGE],
      ArrowRight: ['LEFT', STIKI_MAX_LEFT_EDGE],
      ArrowUp: ['BOTTOM', STIKI_MIN_TOP_EDGE],
      ArrowDown: ['TOP', STIKI_MAX_TOP_EDGE],
    }[direction]

    dispatch(
      alignStikisTo({
        stikiIds: selectedStikis,
        side,
        position,
      })
    )
    dispatch(updateDirtyStikisAsync(pageId))
  }

  const moveSelection = (xDir, yDir, moveDist) => {
    if (!isMine) {
      return
    }

    dispatch(
      addPositionToStikis({
        stikiIds: selectedStikis,
        position: {
          x: xDir * moveDist,
          y: yDir * moveDist,
        },
      })
    )
    dispatch(updateDirtyStikisAsync(pageId))
  }

  const movePage = (xDir, yDir, moveDist) => {
    dispatch(
      addPositionToPage({
        pageId: page.id,
        position: {
          x: xDir * moveDist,
          y: yDir * moveDist,
        },
      })
    )
    dispatch(updatePageAsync(page.id))
  }

  ///////////////////////////////////////////////
  //
  // Event handlers
  //

  const handleKeyDown = (e) => {
    const directions = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    }
    if (Object.keys(directions).includes(e.key)) {
      const xDir = directions[e.key][0]
      const yDir = directions[e.key][1]
      let dist = SELECTION_NUDGE_DIST

      if (selectedStikis.length === 0) {
        dist = PAGE_NUDGE_DIST
        if (e.shiftKey) {
          dist = PAGE_SHIFT_NUDGE_DIST
        }
        movePage(xDir, yDir, dist)
      } else if (e.shiftKey) {
        chuckSelection(e.key)
        e.preventDefault()
        e.stopPropagation()
      } else {
        moveSelection(xDir, yDir, dist)
      }
    }
  }

  const handleMouseDown = (e) => {
    // Stops the browser treating the press as the start of a text selection:
    // a pan let go over the top menu bar used to leave its text selected. A
    // press on a stiki's own text never gets here (the stiki stops it), so
    // selecting text in a stiki still works.
    e.preventDefault()
    focusDomRef()
    setIsMoving(true)
    setMoveStart({ x: e.pageX, y: e.pageY })
  }

  const handleMouseMove = (e) => {
    if (isMoving) {
      setHasMoved(true)
      setMoveOffset({
        x: pxToEm(e.pageX - moveStart.x, page.zoom),
        y: pxToEm(e.pageY - moveStart.y, page.zoom),
      })
    }
  }

  const handleMouseUp = (e) => {
    if (isMoving) {
      setIsMoving(false)

      if (hasMoved) {
        setHasMoved(false)

        dispatch(
          addPositionToPage({
            pageId: page.id,
            position: moveOffset,
          })
        )
        dispatch(updatePageAsync(page.id))
        setMoveStart({ x: 0, y: 0 })
        setMoveOffset({ x: 0, y: 0 })
      } else {
        dispatch(setAllStikisOnPageUnselectedAsync(pageId))
      }
    } else {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  // A wheel has no "finished" event, so the zoom cursor shows while the wheel is
  // turning and clears a moment after it stops. Each turn pushes the moment back,
  // so it stays put during a long scroll and doesn't flicker between turns.
  const showZoomCursor = (cursor) => {
    setZoomCursor(cursor)
    clearTimeout(zoomCursorTimer.current)
    zoomCursorTimer.current = setTimeout(() => setZoomCursor(null), 400)
  }

  useEffect(() => () => clearTimeout(zoomCursorTimer.current), [])

  // Holding shift means a click or a drag selects, so on my own page the
  // cursor says so straight away, not only once a selection box is being
  // dragged. Not while typing in a stiki, where shift is just shift. Watched on
  // the window, since the field rarely has the keyboard focus, and in the
  // capture phase, so a key handler that stops the event (a stiki's text does)
  // can't hide a key-up; dropped when the window loses focus, which would
  // otherwise swallow the key-up too.
  const [shiftSelects, setShiftSelects] = useState(false)

  useEffect(() => {
    if (!isMine) {
      return undefined
    }

    const isTyping = (target) =>
      target?.isContentEditable || ['INPUT', 'TEXTAREA'].includes(target?.tagName)
    const keyDown = (e) => {
      if (e.key === 'Shift' && !isTyping(e.target)) {
        setShiftSelects(true)
      }
    }
    const keyUp = (e) => {
      if (e.key === 'Shift') {
        setShiftSelects(false)
      }
    }
    const blur = () => setShiftSelects(false)

    window.addEventListener('keydown', keyDown, true)
    window.addEventListener('keyup', keyUp, true)
    window.addEventListener('blur', blur)
    return () => {
      window.removeEventListener('keydown', keyDown, true)
      window.removeEventListener('keyup', keyUp, true)
      window.removeEventListener('blur', blur)
    }
  }, [isMine])

  // A pan has to end wherever the mouse is let go, not only over this element.
  // Releasing the button off the field, or over something that swallows the
  // event, used to leave `isMoving` true: the page then kept following the
  // pointer with no button held, and it took a second click to stop it. Watching
  // the window while a pan is in progress ends it wherever it happens.
  useEffect(() => {
    if (!isMoving) {
      return undefined
    }

    const endPan = (e) => handleMouseUp(e)
    window.addEventListener('mouseup', endPan)
    return () => window.removeEventListener('mouseup', endPan)
  }, [isMoving, hasMoved, moveOffset, pageId, page.id])

  const handleDoubleClick = (e) => {
    if (e.target.id !== 'panBackground') {
      return
    }
    const x = e.pageX
    const y = e.pageY
    const { top, right, bottom, left } = e.target.getBoundingClientRect()

    const fieldCentreX = left + (right - left) / 2
    const fieldCentreY = top + (bottom - top) / 2

    const leftEdge = pxToEm(x - fieldCentreX, page.zoom) - 1.5
    const topEdge = pxToEm(y - fieldCentreY, page.zoom) - 1.4

    const stikiData = {
      page_id: page.id,
      stiki: {
        left_edge: leftEdge,
        top_edge: topEdge,
        height: 7,
        width: 15,
        color: currentColor,
      },
    }
    dispatch(createStikiAsync(stikiData))
  }

  const handleWheel = (e) => {
    const minZoom = PAGE_ZOOM_PRESETS[0]
    const maxZoom = PAGE_ZOOM_PRESETS[PAGE_ZOOM_PRESETS.length - 1]

    const percentageChange = 1 + e.deltaY / 100
    const zoomDelta = zoom - zoom * percentageChange
    const clampedDelta = clamp(Math.abs(zoomDelta), 0.01, 0.5)
    const zoomDir = zoomDelta < 0 ? -1 : 1
    const newZoom =
      Math.round(100 * clamp(zoom + clampedDelta * zoomDir, minZoom, maxZoom)) /
      100
    const zoomBy = newZoom - zoom

    // Figure out the zoom offset
    const domRect = domRef.current.getBoundingClientRect()
    const zoomX = e.pageX - domRect.width / 2
    const zoomXAfter = (zoomX * newZoom) / zoom
    const zoomXAdjust = pxToEm(zoomX - zoomXAfter, newZoom)

    const zoomY = e.pageY - domRect.height / 2
    const zoomYAfter = (zoomY * newZoom) / zoom
    const zoomYAdjust = pxToEm(zoomY - zoomYAfter, newZoom)

    if (zoomBy !== 0) {
      showZoomCursor(zoomBy > 0 ? 'zoom-in' : 'zoom-out')

      dispatch(
        zoomByPageAsync({
          pageId,
          zoomBy,
          position: { x: zoomXAdjust, y: zoomYAdjust },
        })
      )
    }
  }

  ///////////////////////////////////////////////
  //
  // Layout constants
  //

  const leftEdge = page.left_edge + moveOffset.x
  const topEdge = page.top_edge + moveOffset.y
  const tabIndex = '0'

  ///////////////////////////////////////////////
  //
  // Markup
  //

  return (
    <Box
      sx={{
        ...classes.stikisField,
        ...(zoomCursor ? classes.zooming(zoomCursor) : {}),
        // A crosshair while shift is held to select, or a selection box is being
        // dragged, over the stikis as well as the page, for the same reason as
        // the zoom cursor.
        ...(isSelecting || shiftSelects ? classes.zooming('crosshair') : {}),
      }}
      className={relativeLayout ? 'relative' : ''}
      style={{ fontSize: PAGE_BASE_FONT_SIZE + 'px' }}
      ref={domRef}
      tabIndex={tabIndex}
      onMouseDown={noMove ? null : handleMouseDown }
      onMouseMove={noMove ? null : handleMouseMove }
      onMouseUp={handleMouseUp}
      onDoubleClick={isMine ? handleDoubleClick : null}

      onWheel={noZoom ? null : handleWheel }
      onKeyDown={handleKeyDown}
    >
      <StikisMoveEventWrapper pageId={pageId}>
        <MarqueeEventWrapper pageId={pageId}>
          <ResizeEventWrapper pageId={pageId}>
            <Box sx={classes.stikisFieldCenter}>
              <Box
                sx={classes.zoomOrigin}
                style={{ fontSize: page.zoom * 100 + '%' }}
              >
                <Box sx={classes.zoomMarker}>
                  <div className='t'></div>
                  <div className='l'></div>
                  <div className='r'></div>
                  <div className='b'></div>
                </Box>
                <Box sx={classes.pageMover}></Box>
                <Box
                  sx={classes.page(leftEdge, topEdge)}
                  className={isMoving ? 'moving' : ''}
                >
                  <Box id='panBackground'
                    sx={classes.panBackground}
                    className={relativeLayout ? 'relative' : ''}
                  ></Box>
                  {unselectedStikis.map((stikiId) => {
                    return <Stiki key={`stiki-${stikiId}`} stikiId={stikiId} />
                  })}
                  <StikisMover pageId={pageId} isMoving={isMoving} />
                  {isSelecting && <SelectionBox />}
                </Box>
              </Box>
            </Box>
          </ResizeEventWrapper>
        </MarqueeEventWrapper>
      </StikisMoveEventWrapper>
    </Box>
  )
}
